/**
 * Button component for Andoria AI
 *
 * A highly flexible and themeable button component using Tailwind CSS and class-variance-authority (cva).
 * Supports multiple variants, sizes, and can render as a native button or as a child component (via Slot).
 *
 * @module Button
 */

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Utility for generating button class names based on variant and size.
 * Uses class-variance-authority (cva) for composable Tailwind classes.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      /**
       * Visual style variants for the button.
       */
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // TODO: Remove 'primary', 'holographic', 'medium', 'large', and 'small' once all components are migrated
        white: 'bg-white text-black border border-gray-300 hover:bg-gray-50',
        primary:
          'bg-gradient-to-r from-[#0f6eea] via-[#0989be] to-[#00ffc3] text-white hover:brightness-110',
        holographic:
          'bg-gradient-to-r from-[#00ffc3] via-[#00a2ff] to-[#0f6eea] text-white shadow hover:shadow-lg',
      },
      /**
       * Size variants for the button.
       */
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        // TODO: Remove or update the 'medium, large, small' button size styling as needed
        medium: 'h-9 px-6 text-sm',
        large: 'px-8 py-4 text-lg',
        small: 'px-4 py-2 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/**
 * Props for the Button component.
 *
 * @property {boolean} [asChild] - If true, renders as a child component (Slot) instead of a native button.
 * @property {"default"|"destructive"|"outline"|"secondary"|"ghost"|"link"} [variant] - Visual style variant.
 * @property {"default"|"sm"|"lg"|"icon"} [size] - Size variant.
 * @property {string} [className] - Additional class names.
 * @property {React.Ref<HTMLButtonElement>} [ref] - Forwarded ref.
 * @property {any} [props] - Other button props.
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

/**
 * Button component definition.
 * Renders a styled button or custom component (via Slot) with variant and size support.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    // Use Slot for polymorphic rendering, otherwise use native button
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

// Export the Button component and the buttonVariants utility
export { Button, buttonVariants };

// TEMPORARY: Add default export for backward compatibility
export default Button;
